import { PrivateKey } from "@bsv/sdk";
import type { BapMasterBackup, MasterBackupType42 } from "bitcoin-backup";
import { encryptBackup } from "bitcoin-backup";
import { syncBackupToStorage } from "@/lib/bap/backup";
import {
	enableNewWalletCloudBackup,
	markCloudBackupStale,
	markNewWalletCloudBackupPending,
} from "@/lib/bap/cloud-refresh";
import {
	saveIdentityDraft,
	writeInitialProfileData,
} from "@/lib/bap/identity-draft";
import {
	deriveSeedProfile,
	generateSeedBackup,
	type SeedBackup,
	validateSeedBackup,
} from "@/lib/bap/seed-contract";
import {
	downloadFile,
	getAccountPubkey,
	initializeBAPFromBackup,
} from "@/lib/bap/utils";
import { logger } from "@/lib/logger";
import { STORAGE_KEYS } from "@/lib/storage-keys";

const preparedSources = new WeakMap<SeedBackup, string | null>();

/** Prepare once, then keep this exact draft through phrase confirmation and retries. */
export function prepareSeedWallet(displayName: string): SeedBackup {
	if (localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP)) {
		throw new Error(
			"A backup is already saved on this device. Keep its encrypted backup file and use a separate browser profile to create a new master, or add a profile from your existing account."
		);
	}
	const prepared = generateSeedBackup(Date.now(), displayName.trim());
	preparedSources.set(prepared, null);
	return prepared;
}

export function isPreparedSeedWalletSaved(prepared: SeedBackup): boolean {
	return preparedSources.get(prepared) != null;
}

export async function persistPreparedSeedWallet(
	prepared: SeedBackup,
	password: string
): Promise<{ backup: SeedBackup; encrypted: string }> {
	if (
		!preparedSources.has(prepared) ||
		localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !==
			preparedSources.get(prepared)
	) {
		throw new Error(
			"Wallet creation was superseded. Preserve the current backup before continuing."
		);
	}
	const backup = validateSeedBackup(prepared);
	const result = await syncBackupToStorage(backup, password, {
		requirePortable: true,
		deferCloudFlush: true,
	});
	if (!(result.success && result.portablePersisted)) {
		throw new Error(result.error || "Your encrypted backup could not be saved");
	}
	const encrypted = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!encrypted) {
		throw new Error("Your encrypted backup is unavailable");
	}
	if (
		sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP) !==
		JSON.stringify(backup)
	) {
		throw new Error("Wallet creation was superseded");
	}
	preparedSources.set(prepared, encrypted);
	sessionStorage.setItem(STORAGE_KEYS.TEMP_PASSWORD, password);
	writeInitialProfileData({ alternateName: backup.label || "" });
	return { backup, encrypted };
}

export const MIN_PASSWORD_LENGTH = 8;

export function validateSignupForm(
	displayName: string,
	password: string,
	confirmPassword: string
): string | null {
	if (!displayName.trim()) {
		return "Display name is required";
	}
	if (password.length < MIN_PASSWORD_LENGTH) {
		return `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
	}
	if (password !== confirmPassword) {
		return "Passwords do not match";
	}
	return null;
}

/** Same name signup gives the downloaded backup: the display name, slugged. */
export function buildBackupFilename(displayName: string): string {
	return `${displayName.trim().replaceAll(" ", "-").toLowerCase()}.bep`;
}

export type CreatedMaster = MasterBackupType42 & {
	label: string;
	createdAt: string;
};

export function createWalletBackup(displayName: string): CreatedMaster {
	return {
		rootPk: PrivateKey.fromRandom().toWif(),
		ids: "",
		createdAt: new Date().toISOString(),
		label: displayName.trim(),
	};
}

function persistEncryptedBackup(params: {
	encrypted: string;
	password: string;
	displayName: string;
	download: boolean;
}): void {
	localStorage.setItem(STORAGE_KEYS.ENCRYPTED_BACKUP, params.encrypted);
	markCloudBackupStale();
	sessionStorage.setItem(STORAGE_KEYS.TEMP_PASSWORD, params.password);
	writeInitialProfileData({ alternateName: params.displayName.trim() });

	if (params.download) {
		downloadFile(
			params.encrypted,
			buildBackupFilename(params.displayName),
			"text/plain"
		);
	}
}

/**
 * Create and store the root key for a new wallet. No download here: the
 * first identity is created during sign-in (prepareBackupForSignIn), and a
 * file saved before that has an empty `ids` and cannot sign in later. Call
 * downloadCurrentEncryptedBackup once sign-in has enrolled the identity.
 */
export async function generateAndEncryptWallet(
	displayName: string,
	password: string
): Promise<{ backup: BapMasterBackup; encrypted: string }> {
	const backup = createWalletBackup(displayName);
	const encrypted = await encryptBackup(backup, password);
	persistEncryptedBackup({
		encrypted,
		password,
		displayName,
		download: false,
	});

	return { backup, encrypted };
}

/**
 * Download the encrypted backup currently held in this browser: after
 * enrollment it carries the identity, which is what a later import needs.
 */
export function downloadCurrentEncryptedBackup(displayName: string): boolean {
	const encrypted = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!encrypted) {
		return false;
	}
	downloadFile(encrypted, buildBackupFilename(displayName), "text/plain");
	return true;
}

async function registerBapIdInDatabase(
	bapId: string,
	name: string,
	accountPubkey: string
): Promise<void> {
	const response = await fetch("/api/user/bap-ids", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ bapId, name, isPrimary: true, accountPubkey }),
	});
	if (!response.ok) {
		throw new Error(
			`Failed to register identity (${response.status}): ${await response.text()}`
		);
	}
}

async function enableCloudBackupAfterIdentityRegistration(
	bapId: string
): Promise<boolean> {
	const result = await enableNewWalletCloudBackup(bapId);
	return result.status === "flushed";
}

export async function createIdentityForCurrentUser(
	displayName: string,
	password: string,
	preparedSeed?: SeedBackup
): Promise<{ bapId: string; cloudBackupEnabled: boolean }> {
	if (preparedSeed) {
		const { backup } = await persistPreparedSeedWallet(preparedSeed, password);
		const first = backup.profiles[0];
		const profile = deriveSeedProfile(backup.mnemonic, first.index);
		markNewWalletCloudBackupPending(profile.bapId);
		await registerBapIdInDatabase(
			profile.bapId,
			displayName.trim(),
			profile.accountPublicKey
		);
		downloadCurrentEncryptedBackup(displayName);
		await saveIdentityDraft(profile.bapId, {
			alternateName: displayName.trim(),
		});
		return {
			bapId: profile.bapId,
			cloudBackupEnabled: await enableCloudBackupAfterIdentityRegistration(
				profile.bapId
			),
		};
	}
	const backup = createWalletBackup(displayName);
	const bap = initializeBAPFromBackup(backup);
	if (!bap) {
		throw new Error("Failed to initialize Bitcoin identity backup");
	}

	const firstIdentity = bap.newId();
	if (!firstIdentity?.bapId) {
		throw new Error("Failed to create Bitcoin identity");
	}

	const accountPubkey = getAccountPubkey(firstIdentity);
	if (!accountPubkey) {
		throw new Error("Failed to derive identity account public key");
	}

	const updatedBackup: BapMasterBackup = {
		...backup,
		ids: bap.exportIds(),
	};
	const encrypted = await encryptBackup(updatedBackup, password);

	localStorage.setItem(STORAGE_KEYS.ENCRYPTED_BACKUP, encrypted);
	markCloudBackupStale();
	markNewWalletCloudBackupPending(firstIdentity.bapId);
	sessionStorage.setItem(
		STORAGE_KEYS.DECRYPTED_BACKUP,
		JSON.stringify(updatedBackup)
	);
	sessionStorage.setItem(STORAGE_KEYS.TEMP_PASSWORD, password);
	writeInitialProfileData({ alternateName: displayName.trim() });
	downloadFile(encrypted, buildBackupFilename(displayName), "text/plain");

	// /api/backup authorizes the session through an owned profile, so register
	// the already-proven BAP identity first. If the subsequent encrypted upload
	// fails, the caller preserves this identity and retries only the upload.
	await registerBapIdInDatabase(
		firstIdentity.bapId,
		displayName.trim(),
		accountPubkey
	);
	// Draft saving is non-fatal: the identity is registered either way.
	const draftResult = await saveIdentityDraft(firstIdentity.bapId, {
		alternateName: displayName.trim(),
	});
	if (!draftResult.success) {
		logger.warn(
			{ bapId: firstIdentity.bapId },
			"Failed to save draft profile for new identity"
		);
	}
	const cloudBackupEnabled = await enableCloudBackupAfterIdentityRegistration(
		firstIdentity.bapId
	);

	return { bapId: firstIdentity.bapId, cloudBackupEnabled };
}
